import React, { useState, useEffect } from "react";
import useSWR from "swr";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { motion } from "framer-motion";
import "react-lazy-load-image-component/src/effects/blur.css";
import { useNavigate } from 'react-router-dom';

const fetcher = (url) =>
  fetch(url, { cache: "no-cache" }).then((res) => res.json());

export default function Hero() {
  const navigate = useNavigate();
  // Translation data
  const lang = {
    "English": "We Stand Together",
    "Pashto": "موږ یوځای ولاړ یو",
    "Dari": "ما با هم ایستاده ایم",
    "Arabic": "نحن نقف معا",
    "Ukrainian": "Ми стоїмо разом",
    "Burmese": "အတူတူရပ်တည်ခဲ့ကြတယ်။",
    "French": "Nous sommes ensemble",
    "Somali": "Waa is garab taaganahay",
    "Sango": "E luti legeoko",
    "Tigrinya": "ብሓባር ደው ንብል",
    "Spanish (feminine)": "Nosotras estamos juntas",
    "Spanish (masculine)": "Nosotros estamos juntos",
    "Kinyarwanda": "Duhagaze hamwe",
    "Chinese (Traditional)": "我們站在一起",
    "Amharic": "አብረን ቆመናል።",
    "Azerbaijani": "Biz birlikdə dayanırıq",
    "Persian": "کنار هم می ایستیم"
  };

  // Convert lang object to an array of translation objects.
  const translations = Object.entries(lang).map(([language, text]) => ({
    language,
    text,
  }));

  // Photos fetching logic
  const apiUrl = process.env.REACT_APP_API_URL;
  const { data: folders, error: folderError } = useSWR(
    `${apiUrl}/api/drive-folders`,
    fetcher,
    { 
      revalidateOnFocus: false,
      dedupingInterval: 600000 // 10 minutes
    }
  );

  const [folderIndex, setFolderIndex] = useState(0);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [randomPhotos, setRandomPhotos] = useState([]);

  useEffect(() => {
    if (folders?.length > 0) {
      const index = folderIndex % folders.length;
      setSelectedFolderId(folders[index].id);
    }
  }, [folders, folderIndex]);

  useEffect(() => {
    if (!folders || folders.length === 0) return;
    const intervalId = setInterval(() => {
      setFolderIndex((prevIndex) => (prevIndex + 1) % folders.length);
    }, 15000);
    return () => clearInterval(intervalId);
  }, [folders]);

  const { data: photos, error: photosError } = useSWR(
    selectedFolderId
      ? `${apiUrl}/api/drive-photos?folderID=${selectedFolderId}&size=medium`
      : null,
    fetcher,
    { 
      revalidateOnFocus: false,
      dedupingInterval: 300000 // 5 minutes
    }
  );

  const pickRandomPhotos = (photosArray) => {
    if (!photosArray?.length) return [];
    // Ensure we get high-quality, visually appealing photos when possible
    return [...photosArray]
      .sort(() => 0.5 - Math.random()) // Shuffle first
      .slice(0, 4);
  };

  useEffect(() => {
    if (photos) setRandomPhotos(pickRandomPhotos(photos));
  }, [photos]);

  // Preload the next folder's random photos
  useEffect(() => {
    if (folders?.length) {
      const nextIndex = (folderIndex + 1) % folders.length;
      const nextFolderId = folders[nextIndex].id;
      fetch(`${apiUrl}/api/drive-photos?folderID=${nextFolderId}&size=medium`, {
        cache: "no-cache",
      })
        .then((res) => res.json())
        .then((nextPhotos) => {
          const nextRandomPhotos = pickRandomPhotos(nextPhotos);
          nextRandomPhotos.forEach((photo) => {
            const img = new window.Image();
            img.src = `${apiUrl}${photo.thumbnailUrl}`;
          });
        })
        .catch((err) => {
          console.error("Error preloading next folder images:", err);
        });
    }
  }, [folders, folderIndex, apiUrl]);

  // Typewriter component
  const Typewriter = () => {
    const [displayedText, setDisplayedText] = useState("");
    const [isDeleting, setIsDeleting] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
      const fullText = translations[currentIndex].text;
      let timer;

      if (!isDeleting && displayedText === fullText) {
        timer = setTimeout(() => setIsDeleting(true), 2000);
      } else {
        const delta = isDeleting ? 75 : 150;
        timer = setTimeout(() => {
          const newText = isDeleting
            ? fullText.substring(0, displayedText.length - 1)
            : fullText.substring(0, displayedText.length + 1);
          setDisplayedText(newText);

          if (isDeleting && newText === "") {
            setIsDeleting(false);
            setCurrentIndex((prevIndex) => (prevIndex + 1) % translations.length);
          }
        }, delta);
      }
      return () => clearTimeout(timer);
    }, [displayedText, isDeleting, currentIndex]);

    return (
      <div className="mb-8">
        <h2 className="text-secondary text-left font-display">
          <span className="text-h3 text-primary font-medium">
            {translations[currentIndex].language}:
          </span>{" "}
          <span className="text-h2 min-h-[3rem] inline-block">
            {displayedText}
            <span className="animate-pulse ml-1">|</span>
          </span>
        </h2>
      </div>
    );
  };
  
  // Rotations and styling for photos
  const photoStyles = [
    { rotation: "rotate-[-2deg]", zIndex: "z-20", className: "origin-top-left" },
    { rotation: "rotate-[2deg]", zIndex: "z-10", className: "origin-top-right" },
    { rotation: "rotate-[1deg]", zIndex: "z-30", className: "origin-bottom-left" },
    { rotation: "rotate-[-1deg]", zIndex: "z-20", className: "origin-bottom-right" }
  ];

  // Error component for the right side
  const PhotosErrorState = () => (
    <div className="flex flex-col items-center justify-center w-full h-full">
      <div className="bg-background/80 backdrop-blur-sm p-6 rounded-xl shadow-md">
        <h3 className="text-h4 font-display text-secondary mb-2">Something went wrong</h3>
        <p className="text-textPrimary">Unable to load photos. Please try again later.</p>
      </div>
      {/* Decorative elements remain visible during error state */}
      <div className="absolute w-32 h-32 bg-primary opacity-20 rounded-full -right-10 -top-10 z-0"></div>
      <div className="absolute w-48 h-48 bg-cta opacity-10 rounded-full -left-12 -bottom-24 z-0"></div>
    </div>
  );

  return (
    <div id="hero" className="w-full min-h-screen max-w-full mx-auto px-6 sm:px-8 md:px-16 lg:px-24 xl:px-32 flex flex-col lg:flex-row items-center justify-between gap-8 md:gap-12 lg:gap-16 pt-6 pb-16 relative">
      {/* Left side - Text content */}
      <motion.div 
        className="w-full lg:w-2/5 z-10 pt-8 lg:pt-0 flex-shrink-0"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
      >
        <h1 className="text-h1 md:text-title font-display font-bold text-secondary mb-4 leading-tight text-left">
          THE FUTURE <br />
          <span className="relative">
            BELONGS TO US
            <div className="absolute -bottom-2 left-0 h-1 w-24 bg-cta"></div>
          </span>
        </h1>
        
        <Typewriter />
        
        <p className="text-bodyLarge text-textPrimary mb-8 max-w-xl">
          Students for Refugees empowers displaced youth through education, mentorship, and advocacy, 
          creating pathways to success for refugee students worldwide.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-6 mt-8">
          <motion.button 
            className="bg-cta text-background py-4 px-8 rounded-xl text-button font-body transition-all duration-300 hover:shadow-xl hover:scale-105 backdrop-blur-sm flex items-center justify-center"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              navigate('/donate');
            }}
          >
            Donate Now
          </motion.button>
          <motion.button 
            className="bg-primary text-background py-4 px-8 rounded-xl text-button font-body transition-all duration-300 hover:shadow-xl hover:scale-105 backdrop-blur-sm flex items-center justify-center"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              const element = document.getElementById('vision');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}  
          >
            Learn More
          </motion.button>
        </div>
      </motion.div>
      
      {/* Right side - Photos masonry */}
      <div className="w-full lg:w-3/5 h-auto min-h-[400px] md:min-h-[500px] lg:min-h-[600px] mt-6 lg:-mt-3 relative flex items-center justify-center">
        {/* Show loading indicator when loading */}
        {!photos && !folderError && !photosError && (
          <div className="flex items-center justify-center h-full w-full">
            <div className="w-12 h-12 md:w-16 md:h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
        
        {/* Show error state if either folder or photos error occurs */}
        {(folderError || photosError) && <PhotosErrorState />}
        
        {/* Photos in masonry-like layout - Only show when no errors and photos are available */}
        {!folderError && !photosError && randomPhotos.length > 0 && (
          <div className="relative w-full h-full">
            <div className="photo-masonry-container relative h-[50vh] sm:h-[55vh] md:h-[60vh] lg:h-[70vh] max-h-[700px]">
              {randomPhotos.map((photo, index) => {
                // Define positions and styles for each photo
                const positions = [
                  {
                    className: `absolute top-0 right-0 w-[60%] h-[48%] max-h-[300px] ${photoStyles[0].zIndex} ${photoStyles[0].rotation} ${photoStyles[0].className}`,
                    maxWidth: "500px",
                    maxHeight: "300px",
                    delay: 0.1
                  },
                  {
                    className: `absolute top-[10%] left-0 w-[48%] h-[45%] max-h-[280px] ${photoStyles[1].zIndex} ${photoStyles[1].rotation} ${photoStyles[1].className}`,
                    maxWidth: "400px",
                    maxHeight: "280px",
                    delay: 0.2
                  },
                  {
                    className: `absolute bottom-0 left-0 w-[50%] h-[48%] max-h-[290px] ${photoStyles[2].zIndex} ${photoStyles[2].rotation} ${photoStyles[2].className}`,
                    maxWidth: "420px",
                    maxHeight: "290px",
                    delay: 0.3
                  },
                  {
                    className: `absolute bottom-[3%] right-0 w-[58%] h-[46%] max-h-[285px] ${photoStyles[3].zIndex} ${photoStyles[3].rotation} ${photoStyles[3].className}`,
                    maxWidth: "480px",
                    maxHeight: "285px",
                    delay: 0.4
                  }
                ];
                
                const pos = positions[index];
                
                return (
                  <motion.div
                    key={photo.id || index}
                    className={pos.className}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ 
                      opacity: 1, 
                      y: 0, 
                      rotate: photoStyles[index].rotation.replace("rotate-[", "").replace("deg]", "")
                    }}
                    transition={{ 
                      duration: 0.7, 
                      delay: pos.delay,
                      type: "spring",
                      stiffness: 100
                    }}
                  >
                    <div 
                      className="w-full h-full overflow-hidden rounded-2xl shadow-lg border-2 border-white"
                      style={{ 
                        maxWidth: pos.maxWidth, 
                        maxHeight: pos.maxHeight,
                        backgroundColor: 'transparent' 
                      }}
                    >
                      <LazyLoadImage
                        src={`${apiUrl}${photo.thumbnailUrl}`}
                        alt={photo.name || `Refugees photo ${index + 1}`}
                        effect="opacity"
                        placeholderSrc="/placeholder-image.jpg"
                        wrapperClassName="w-full h-full"
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Decorative elements - always show to maintain design consistency */}
        <div className="absolute w-32 h-32 bg-primary opacity-20 rounded-full -right-10 -top-10 z-0"></div>
        <div className="absolute w-48 h-48 bg-cta opacity-10 rounded-full -left-12 -bottom-24 z-0"></div>
      </div>
    </div>
  );
}